import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './theme.css';
import Dashboard from './main/dashboard';
import Login from './main/login';
import LinkedInPoster from './linkedInPoster/LinkedInPoster';
import Posts from './linkedInPoster/Posts';
import AIPromptSettings from './linkedInPoster/AIPromptSettings';
import LinkedInCallback from './linkedInPoster/LinkedInCallback';
import Scheduler from './linkedInPoster/Scheduler';
import Users from './admin/users';
import { auth, db, requestNotificationPermission, onMessageListener } from './firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import SubscriptionPlans from './subscription-system/SubscriptionPlans';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Organization from './organization/Organization';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { CustomerSwitchProvider, useCustomerSwitch } from './contexts/CustomerSwitchContext';
import Register from './main/register';
import VerifyEmail from './main/verifyEmail';
import Billedearkiv from './linkedInPoster/billedearkiv';
import AIGenerator from './linkedInPoster/components/AIGenerator';
import AcceptInvitation from './organization/AcceptInvitation';
import CaseGenerator from './linkedInPoster/components/CaseGenerator';
import { LanguageProvider } from './contexts/LanguageContext';

// SubscriptionRoute - begrænser adgang til brugere med aktivt abonnement eller licens
const SubscriptionRoute = ({ children }) => {
  const { hasAccess, loading } = useSubscription();
  const navigate = useNavigate();
  
  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (!hasAccess) {
    return <Navigate to="/linkedin-poster/subscribe" state={{ message: "Du skal have et aktivt abonnement eller licens for at tilgå denne side" }} />;
  }
  
  return children;
};

// EmployeeRoute - begrænser adgang til kun medarbejdere
const EmployeeRoute = ({ children }) => {
  const { currentUser, userType } = useAuth();
  const { isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    
    // Tjek om brugerens e-mail er bekræftet
    const checkVerification = async () => {
      if (auth.currentUser) {
        // Genindlæs brugeren for at få den nyeste status
        await auth.currentUser.reload();
        setIsVerified(auth.currentUser.emailVerified);
      }
      setLoading(false);
    };
    
    checkVerification();
  }, [currentUser]);
  
  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!isVerified) {
    return <Navigate to="/verify-email" />;
  }
  
  // Omdirigér kunder til indstillinger i stedet for dashboard
  if (userType === 'customer') {
    return <Navigate to="/linkedin-settings" state={{ message: "Som kunde har du kun adgang til LinkedIn-indstillinger" }} />;
  }
  
  return children;
};

// RestrictedWhenWorkingForCustomer - begrænser adgang til visse sider når man arbejder på vegne af kunde
const RestrictedWhenWorkingForCustomer = ({ children, path }) => {
  const { currentUser, userType } = useAuth();
  const { isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const navigate = useNavigate();
  const location = useLocation();

  // Afgør om denne side skal begrænses når man arbejder på vegne af en kunde
  const isRestrictedPath = ['/linkedin-settings', '/organization'].includes(path);
  
  // Hvis vi arbejder på vegne af en kunde og forsøger at tilgå en begrænset side
  if (isWorkingOnBehalfOfCustomer && isRestrictedPath) {
    return <Navigate to="/ai-generator" state={{ 
      message: "Du kan ikke tilgå denne side når du arbejder på vegne af en kunde" 
    }} />;
  }
  
  return children;
};

// CustomerAllowedRoute - tillader både kunder og medarbejdere
const CustomerAllowedRoute = ({ children, path }) => {
  const { currentUser, userType } = useAuth();
  const { isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    
    // Tjek om brugerens e-mail er bekræftet
    const checkVerification = async () => {
      if (auth.currentUser) {
        // Genindlæs brugeren for at få den nyeste status
        await auth.currentUser.reload();
        setIsVerified(auth.currentUser.emailVerified);
      }
      setLoading(false);
    };
    
    checkVerification();
  }, [currentUser]);
  
  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!isVerified) {
    return <Navigate to="/verify-email" />;
  }

  // Hvis en kunde prøver at få adgang til en anden side end /linkedin-settings, omdiriger dem
  if (userType === 'customer' && path && path !== '/linkedin-settings') {
    return <Navigate to="/linkedin-settings" />;
  }
  
  // Hvis vi arbejder på vegne af en kunde og forsøger at tilgå indstillinger eller organisation
  if (isWorkingOnBehalfOfCustomer && path && (path === '/linkedin-settings' || path === '/organization')) {
    return <Navigate to="/ai-generator" state={{ 
      message: "Du kan ikke tilgå denne side når du arbejder på vegne af en kunde" 
    }} />;
  }
  
  return children;
};

// PrivateRoute komponent til at beskytte routes
const PrivateRoute = ({ children, isAuthenticated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    // Tjek om brugerens e-mail er bekræftet
    const checkVerification = async () => {
      if (auth.currentUser) {
        // Genindlæs brugeren for at få den nyeste status
        await auth.currentUser.reload();
        setIsVerified(auth.currentUser.emailVerified);
      }
      setLoading(false);
    };
    
    checkVerification();
  }, [isAuthenticated]);
  
  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isVerified) {
    return <Navigate to="/verify-email" />;
  }
  
  return children;
};

// LinkedInRoute komponent til at beskytte LinkedIn-relaterede routes
const LinkedInRoute = ({ children, path }) => {
  const { hasAccess } = useSubscription();
  
  if (!hasAccess && path !== '/linkedin-poster/subscribe') {
    return <Navigate to="/linkedin-poster/subscribe" state={{ message: "Du skal have et aktivt abonnement eller licens for at tilgå denne side" }} />;
  }
  
  return <CustomerAllowedRoute path={path}>{children}</CustomerAllowedRoute>
};

// RouteWithAccess komponent til at beskytte routes
const RouteWithAccess = ({ component, isAuthenticated, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const { hasAccess, loading: subscriptionLoading } = useSubscription();

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    // Tjek om brugerens e-mail er bekræftet
    const checkVerification = async () => {
      if (auth.currentUser) {
        // Genindlæs brugeren for at få den nyeste status
        await auth.currentUser.reload();
        setIsVerified(auth.currentUser.emailVerified);
      }
      setLoading(false);
    };
    
    checkVerification();
  }, [isAuthenticated]);
  
  if (loading || subscriptionLoading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isVerified) {
    return <Navigate to="/verify-email" />;
  }
  
  if (!hasAccess) {
    return <Navigate to="/linkedin-poster/subscribe" state={{ message: "Du skal have et aktivt abonnement eller licens for at tilgå denne side" }} />;
  }
  
  return component;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState(false);

  useEffect(() => {
    // Tjek bruger-authentikation og opdater tilstand
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Tjek først om vi er på accept-invitation siden - hvis ja, gør intet med auth state
      const currentPath = window.location.pathname;
      const currentUrl = window.location.href;
      const isOnSpecialAuthPage = currentPath.includes('/accept-invitation') || 
                                 currentUrl.includes('/accept-invitation') ||
                                 currentPath.includes('/linkedin-callback') || 
                                 currentUrl.includes('/linkedin-callback') ||
                                 (currentPath === '/' && new URLSearchParams(window.location.search).has('code'));
      
      console.log("Auth state changed. Current path:", currentPath);
      console.log("Is on special auth page:", isOnSpecialAuthPage);
      
      if (isOnSpecialAuthPage) {
        // Ved special auth side, lad os afslutte loading uden at redirecte
        console.log("På special auth side - undgår auth redirect");
        setLoading(false);
        return;
      }
      
      if (user) {
        setIsAuthenticated(true);
        
        // Anmod om notifikationstilladelse og registrer FCM-token
        try {
          const permissionResult = await requestNotificationPermission(user.uid);
          setNotificationPermission(permissionResult.success);
          if (permissionResult.success) {
            console.log('Notifikationstilladelse givet og FCM-token registreret');
          } else {
            console.warn('Kunne ikke aktivere notifikationer:', permissionResult.error);
          }
        } catch (error) {
          console.error('Fejl ved opsætning af notifikationer:', error);
        }
        
        // Hent brugerdata fra Firestore for at tjekke verificeringsstatus osv.
        // ... existing code for user data fetching ...
      } else {
        setIsAuthenticated(false);
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Lyt efter forgrundsbeskeder, når brugeren er autentificeret
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // onMessageListener returnerer en Promise, ikke en unsubscribe-funktion
    onMessageListener()
      .then((payload) => {
        if (payload) {
          const { notification } = payload;
          
          // Vis notifikation i browser, selv når appen er åben
          if ('Notification' in window && Notification.permission === 'granted') {
            const notifOptions = {
              body: notification.body,
              icon: '/logo192.png'
            };
            
            const notif = new Notification(notification.title, notifOptions);
            
            // Håndter klik på notifikation
            notif.onclick = () => {
              window.focus();
              notif.close();
              
              // Naviger til den relevante side baseret på data
              const clickAction = payload.data?.clickAction || '/ai-generator';
              window.location.href = clickAction;
            };
          }
        }
      })
      .catch(err => console.error('Fejl ved modtagelse af forgrundsbeskeder:', err));
    
    // Da onMessageListener ikke returnerer en unsubscribe-funktion, 
    // returnerer vi en tom cleanup-funktion
    return () => {
      // Ingen cleanup nødvendig for onMessageListener
    };
  }, [isAuthenticated]);

  // Funktion til at håndtere login
  const handleLogin = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Tjek om brugerens email er bekræftet
      if (!user.emailVerified) {
        return {
          success: true,
          redirect: '/verify-email'
        };
      }
      
      // Tjek brugerens type og omdiriger baseret på type
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Kunder sendes til LinkedIn-indstillinger, ikke til dashboard
          if (userData.userType === 'customer') {
            return {
              success: true,
              redirect: '/linkedin-settings'
            };
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af brugerdata:', error);
      }
      
      return { success: true };
    } catch (error) {
      let errorMessage = 'Forkert email eller adgangskode';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Forkert email eller adgangskode';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'For mange mislykkede forsøg. Prøv igen senere.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Funktion til at håndtere logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout fejl:', error);
    }
  };

  // Håndter OAuth callback
  const handleOAuthCallback = (location, userType) => {
    // Tjek om URL'en indeholder en authorization code
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('code') || searchParams.has('error')) {
      console.log('OAuth callback detekteret på root path:', location.search);
      return <LinkedInCallback />;
    }
    
    // Kunder sendes til LinkedIn-indstillinger, ansatte til dashboard
    if (isAuthenticated) {
      return userType === 'customer' 
        ? <Navigate to="/linkedin-settings" />
        : <Navigate to="/" state={{ skipSubscriptionCheck: true }} />;
    }
    
    return <Navigate to="/login" />;
  };

  // Vis loading-indikator mens Firebase auth initialiseres
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <CustomerSwitchProvider>
          <LanguageProvider>
            <Router>
              <Routes>
                <Route path="/login" element={
                  isAuthenticated ? 
                    <Navigate to="/" /> : 
                    <Login onLogin={handleLogin} />
                } />
                <Route 
                  path="/" 
                  element={<RouteWithCallback 
                    isAuthenticated={isAuthenticated} 
                    onLogout={handleLogout} 
                    callbackHandler={handleOAuthCallback}
                  />} 
                />
                <Route 
                  path="/linkedin-settings" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <CustomerAllowedRoute path="/linkedin-settings">
                          <RestrictedWhenWorkingForCustomer path="/linkedin-settings">
                            <LinkedInPoster onLogout={handleLogout} />
                          </RestrictedWhenWorkingForCustomer>
                        </CustomerAllowedRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/billedearkiv" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <LinkedInRoute path="/billedearkiv">
                          <Billedearkiv onLogout={handleLogout} />
                        </LinkedInRoute>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/linkedin-posts" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <LinkedInRoute path="/linkedin-posts">
                          <Posts onLogout={handleLogout} />
                        </LinkedInRoute>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/ai-settings" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <LinkedInRoute path="/ai-settings">
                          <AIPromptSettings onLogout={handleLogout} />
                        </LinkedInRoute>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/scheduler" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <LinkedInRoute path="/scheduler">
                          <Scheduler onLogout={handleLogout} />
                        </LinkedInRoute>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/linkedin-callback" 
                  element={
                    <LinkedInCallback />
                  } 
                />
                <Route 
                  path="/users" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <LinkedInRoute path="/users">
                          <Users onLogout={handleLogout} />
                        </LinkedInRoute>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
                <Route 
                  path="/linkedin-poster/subscribe" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionPlans />
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/subscribe" 
                  element={
                    isAuthenticated ? (
                      <Navigate to="/linkedin-poster/subscribe" />
                    ) : (
                      <Navigate to="/login" />
                    )
                  } 
                />
                <Route 
                  path="/organization" 
                  element={
                    isAuthenticated ? (
                      <SubscriptionRoute>
                        <EmployeeRoute>
                          <RestrictedWhenWorkingForCustomer path="/organization">
                            <Organization onLogout={handleLogout} />
                          </RestrictedWhenWorkingForCustomer>
                        </EmployeeRoute>
                      </SubscriptionRoute>
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
                <Route path="/register" element={
                  <Register />
                } />
                <Route path="/verify-email" element={
                  <VerifyEmail />
                } />
                <Route path="/ai-generator" element={
                  <RouteWithAccess
                    component={
                      <EmployeeRoute>
                        <AIGenerator onLogout={handleLogout} />
                      </EmployeeRoute>
                    }
                    isAuthenticated={isAuthenticated}
                    onLogout={handleLogout}
                  />
                } />
                <Route path="/case-generator" element={
                  <RouteWithAccess
                    component={
                      <EmployeeRoute>
                        <CaseGenerator onLogout={handleLogout} />
                      </EmployeeRoute>
                    }
                    isAuthenticated={isAuthenticated}
                    onLogout={handleLogout}
                  />
                } />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Router>
          </LanguageProvider>
        </CustomerSwitchProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

// RouteWithCallback komponent
function RouteWithCallback({ isAuthenticated, onLogout, callbackHandler }) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();
  const { userType } = useAuth();
  const { hasAccess, loading: subscriptionLoading } = useSubscription();
  const skipCheck = location?.state?.skipSubscriptionCheck;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    // Tjek om brugerens e-mail er bekræftet
    const checkVerification = async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        setIsVerified(auth.currentUser.emailVerified);
      }
      setLoading(false);
    };
    
    checkVerification();
  }, [isAuthenticated]);
  
  if (loading || subscriptionLoading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }
  
  if (isAuthenticated && !isVerified) {
    return <Navigate to="/verify-email" />;
  }
  
  // Ved rod-stien '/', vis dashboard uden at tjekke abonnement (det håndteres af Dashboard komponenten selv)
  if (location.pathname === '/' && isAuthenticated && isVerified) {
    return userType === 'customer' 
      ? <Navigate to="/linkedin-settings" />
      : <Dashboard onLogout={onLogout} />;
  }
  
  const callback = callbackHandler || (() => {});
  if (typeof callback === 'function') {
    return callback(location, userType);
  } else {
    console.error('Callback er ikke en funktion', callback);
    // Returner en fallback-komponent
    return <Navigate to="/" />;
  }
}

export default App;
